import { useState } from 'react';
import { Shield, Truck, RefreshCw, FileText } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const TABS = [
  { id: 'privacidade', label: 'Privacidade e Cookies', icon: Shield },
  { id: 'entrega', label: 'Entrega e Frete', icon: Truck },
  { id: 'troca', label: 'Troca e Devolução', icon: RefreshCw },
  { id: 'termos', label: 'Termos de Uso', icon: FileText },
];

export default function PoliciesPage() {
  const [activeTab, setActiveTab] = useState('privacidade');

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 w-full">
        <h1 className="text-3xl md:text-5xl font-bold text-white uppercase tracking-tight mb-8" style={{ fontFamily: 'Teko, sans-serif' }} data-testid="policies-title">
          Políticas
        </h1>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="policies-tabs">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  activeTab === tab.id
                    ? 'bg-red-600 text-white'
                    : 'bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-6 md:p-8" data-testid="policies-content">
          {activeTab === 'privacidade' && <PrivacidadeContent />}
          {activeTab === 'entrega' && <EntregaContent />}
          {activeTab === 'troca' && <TrocaContent />}
          {activeTab === 'termos' && <TermosContent />}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function SectionTitle({ children }) {
  return <h2 className="text-xl font-bold text-white mb-4 mt-6 first:mt-0">{children}</h2>;
}

function Paragraph({ children }) {
  return <p className="text-zinc-400 text-sm leading-relaxed mb-3">{children}</p>;
}

function ListItem({ children }) {
  return <li className="text-zinc-400 text-sm leading-relaxed">{children}</li>;
}

function BulletList({ children }) {
  return <ul className="list-disc list-inside space-y-1 mb-4 ml-2">{children}</ul>;
}

function PrivacidadeContent() {
  return (
    <div data-testid="content-privacidade">
      <SectionTitle>Política de Privacidade e Cookies</SectionTitle>
      <Paragraph>
        A MOTOVELOZ respeita sua privacidade e está comprometida com a proteção dos seus dados pessoais conforme a Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018).
      </Paragraph>

      <SectionTitle>Quais dados coletamos:</SectionTitle>
      <BulletList>
        <ListItem>Nome e informações de contato fornecidos no cadastro</ListItem>
        <ListItem>Localização compartilhada voluntariamente para cálculo de frete e entrega</ListItem>
        <ListItem>Dados de navegação via cookies para melhorar sua experiência</ListItem>
      </BulletList>

      <SectionTitle>Como usamos seus dados:</SectionTitle>
      <BulletList>
        <ListItem>Para processar e entregar seus pedidos</ListItem>
        <ListItem>Para calcular o frete com base na sua localização</ListItem>
        <ListItem>Para entrar em contato via WhatsApp sobre seu pedido</ListItem>
        <ListItem>Para melhorar nosso site e serviços</ListItem>
      </BulletList>

      <SectionTitle>Cookies:</SectionTitle>
      <Paragraph>
        Utilizamos cookies para manter seu carrinho ativo, lembrar seu login e melhorar sua experiência de navegação. Ao continuar navegando, você concorda com o uso de cookies.
      </Paragraph>

      <SectionTitle>Seus direitos pela LGPD:</SectionTitle>
      <BulletList>
        <ListItem>Acessar seus dados pessoais</ListItem>
        <ListItem>Corrigir dados incorretos</ListItem>
        <ListItem>Solicitar exclusão dos seus dados</ListItem>
        <ListItem>Revogar consentimento a qualquer momento</ListItem>
      </BulletList>

      <Paragraph>
        Para exercer seus direitos, entre em contato pelo WhatsApp <span className="text-red-500 font-semibold">(86) 9 9816-6143</span>.
      </Paragraph>
      <Paragraph>
        Não compartilhamos seus dados com terceiros, exceto quando exigido por lei. Dados de localização são usados exclusivamente para entrega e não são armazenados após a conclusão do pedido.
      </Paragraph>
    </div>
  );
}

function EntregaContent() {
  return (
    <div data-testid="content-entrega">
      <SectionTitle>Política de Entrega e Frete</SectionTitle>

      <SectionTitle>Como funciona a entrega:</SectionTitle>
      <Paragraph>
        A MOTOVELOZ realiza entregas em domicílio na região de Água Branca e Miguel Alves - PI e municípios próximos. O frete é calculado automaticamente com base na distância entre nossa loja e o endereço de entrega informado pelo cliente.
      </Paragraph>

      <SectionTitle>Prazo de entrega:</SectionTitle>
      <BulletList>
        <ListItem>Pedidos confirmados até as 12h: entrega no mesmo dia (sujeito à disponibilidade)</ListItem>
        <ListItem>Pedidos confirmados após as 12h: entrega no próximo dia útil</ListItem>
        <ListItem>Regiões mais distantes: prazo de 1 a 3 dias úteis</ListItem>
      </BulletList>

      <SectionTitle>Cálculo do frete:</SectionTitle>
      <Paragraph>
        O valor do frete é calculado automaticamente por quilômetro rodado e exibido antes da confirmação do pedido via WhatsApp. O cliente pode visualizar a distância e o valor antes de confirmar.
      </Paragraph>

      <SectionTitle>Retirada na loja:</SectionTitle>
      <Paragraph>
        O cliente também pode optar por retirar o produto diretamente em nossa loja em Água Branca - PI, sem custo de frete.
      </Paragraph>

      <SectionTitle>Rastreamento:</SectionTitle>
      <Paragraph>
        O acompanhamento do pedido é feito diretamente pelo WhatsApp <span className="text-red-500 font-semibold">(86) 9 9816-6143</span>, onde nossa equipe mantém o cliente informado sobre o status da entrega.
      </Paragraph>

      <SectionTitle>Produtos indisponíveis:</SectionTitle>
      <Paragraph>
        Caso algum produto do pedido esteja indisponível após a confirmação, entraremos em contato imediatamente pelo WhatsApp para oferecer uma alternativa ou realizar o cancelamento e reembolso integral.
      </Paragraph>
    </div>
  );
}

function TrocaContent() {
  return (
    <div data-testid="content-troca">
      <SectionTitle>Política de Troca e Devolução</SectionTitle>
      <Paragraph>
        A MOTOVELOZ segue rigorosamente o Código de Defesa do Consumidor (Lei nº 8.078/1990).
      </Paragraph>

      <SectionTitle>Direito de arrependimento:</SectionTitle>
      <Paragraph>
        Conforme o artigo 49 do CDC, o cliente tem direito de desistir da compra em até 7 dias corridos após o recebimento do produto, sem necessidade de justificativa, com reembolso integral do valor pago incluindo o frete.
      </Paragraph>

      <SectionTitle>Como solicitar troca ou devolução:</SectionTitle>
      <Paragraph>
        Entre em contato pelo WhatsApp <span className="text-red-500 font-semibold">(86) 9 9816-6143</span> informando:
      </Paragraph>
      <BulletList>
        <ListItem>Número do pedido</ListItem>
        <ListItem>Motivo da troca ou devolução</ListItem>
        <ListItem>Fotos do produto (em caso de defeito)</ListItem>
      </BulletList>

      <SectionTitle>Condições para troca ou devolução:</SectionTitle>
      <BulletList>
        <ListItem>Produto deve estar na embalagem original</ListItem>
        <ListItem>Sem sinais de uso ou danos causados pelo cliente</ListItem>
        <ListItem>Acompanhado de todos os acessórios e documentos originais</ListItem>
      </BulletList>

      <SectionTitle>Produtos com defeito:</SectionTitle>
      <Paragraph>
        Produtos com defeito de fabricação têm garantia mínima de 90 dias conforme o CDC. Em caso de defeito, realizamos a troca imediata ou reembolso integral.
      </Paragraph>

      <SectionTitle>Prazo de reembolso:</SectionTitle>
      <Paragraph>
        O reembolso é realizado em até 5 dias úteis após a confirmação da devolução, pelo mesmo meio de pagamento utilizado na compra.
      </Paragraph>

      <SectionTitle>Produtos que não podem ser trocados:</SectionTitle>
      <BulletList>
        <ListItem>Produtos danificados por mau uso</ListItem>
        <ListItem>Produtos sem embalagem original</ListItem>
        <ListItem>Peças instaladas que apresentem sinais de uso</ListItem>
      </BulletList>
    </div>
  );
}

function TermosContent() {
  return (
    <div data-testid="content-termos">
      <SectionTitle>Termos de Uso</SectionTitle>
      <Paragraph>
        Ao acessar e utilizar o site motovelozoficial.com.br, você concorda com os seguintes termos:
      </Paragraph>

      <SectionTitle>Sobre o site:</SectionTitle>
      <Paragraph>
        O site MOTOVELOZ é uma plataforma de comércio eletrônico de peças e acessórios para motocicletas, localizada em Água Branca, Miguel Alves - PI, operada por pessoa física/jurídica sob as leis brasileiras.
      </Paragraph>

      <SectionTitle>Uso adequado:</SectionTitle>
      <Paragraph>
        O usuário se compromete a utilizar o site apenas para fins legais e legítimos, não realizando ações que possam prejudicar outros usuários ou o funcionamento do site.
      </Paragraph>

      <SectionTitle>Preços e disponibilidade:</SectionTitle>
      <Paragraph>
        Os preços exibidos no site estão em reais (R$) e podem ser alterados sem aviso prévio. A disponibilidade dos produtos é atualizada regularmente, mas pode haver variações de estoque.
      </Paragraph>

      <SectionTitle>Processo de compra:</SectionTitle>
      <Paragraph>
        A compra é finalizada via WhatsApp após o cliente enviar o carrinho. O contrato de compra e venda é estabelecido somente após a confirmação do pedido pela MOTOVELOZ via WhatsApp.
      </Paragraph>

      <SectionTitle>Propriedade intelectual:</SectionTitle>
      <Paragraph>
        Todo o conteúdo do site, incluindo textos, imagens, logotipos e layout, é de propriedade da MOTOVELOZ e não pode ser reproduzido sem autorização.
      </Paragraph>

      <SectionTitle>Limitação de responsabilidade:</SectionTitle>
      <Paragraph>
        A MOTOVELOZ não se responsabiliza por danos causados pelo uso incorreto dos produtos. As especificações técnicas são fornecidas pelos fabricantes e devem ser verificadas pelo cliente antes da compra.
      </Paragraph>

      <SectionTitle>Foro:</SectionTitle>
      <Paragraph>
        Fica eleito o foro da comarca de Água Branca - PI para dirimir quaisquer conflitos oriundos destes termos.
      </Paragraph>

      <SectionTitle>Contato:</SectionTitle>
      <Paragraph>
        Para dúvidas sobre estes termos, entre em contato pelo WhatsApp <span className="text-red-500 font-semibold">(86) 9 9816-6143</span> ou pelo e-mail disponível no site.
      </Paragraph>

      <Paragraph>
        <span className="text-zinc-500 text-xs">Última atualização: abril de 2026.</span>
      </Paragraph>
    </div>
  );
}
